<script lang="ts" setup>
const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { submitConsultation } = useConsultationApi()

definePageMeta({ layout: 'page' })
useHead({
  title: t('productHardware.consultationMessage.title'),
  meta: [
    {
      name: 'description',
      content: t('seo.consultation.description'),
    },
    {
      name: 'keywords',
      content: t('seo.consultation.keywords'),
    },
    {
      property: 'og:title',
      content: t('seo.consultation.title'),
    },
    {
      property: 'og:description',
      content: t('seo.consultation.description'),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      property: 'og:locale',
      content: 'zh_CN',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: t('seo.consultation.title'),
    },
    {
      name: 'twitter:description',
      content: t('seo.consultation.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      name: 'author',
      content: '玻色量子',
    },
  ],
})

// 表单数据
const userInfoFormData = ref({
  name: '',
  phone: '',
  email: '',
  company: '',
  industry: '',
  message: '',
})

// 提交状态
const isSubmitting = ref(false)

// 错误信息
const errors = ref({
  name: '',
  phone: '',
  email: '',
  company: '',
  industry: '',
  message: '',
})

// 追踪哪些字段已被触碰过（用于决定是否显示错误）
const touchedFields = ref({
  name: false,
  phone: false,
  email: false,
  company: false,
  industry: false,
  message: false,
})

// 邮箱验证规则
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

// 验证规则配置
const validationRules = {
  name: [
    {
      validator: (value: string) => !!value.trim(),
      message: '请输入您的名字',
    },
  ],
  phone: [
    {
      validator: (value: string) => !!value.trim(),
      message: '请输入您的电话',
    },
    {
      validator: (value: string) => value.length === 11,
      message: '请输入11位手机号码',
    },
    {
      validator: (value: string) => /^1[3-9]\d{9}$/.test(value),
      message: '请输入有效的手机号码',
    },
  ],
  email: [
    {
      validator: (value: string) => !!value.trim(),
      message: '请输入您的电子邮箱',
    },
    {
      validator: (value: string) => emailRegex.test(value),
      message: '请输入有效的电子邮箱',
    },
  ],
  company: [
    {
      validator: (value: string) => !!value.trim(),
      message: '请输入您的单位',
    },
  ],
  industry: [
    {
      validator: (value: string) => !!value.trim(),
      message: '请输入您所在的行业',
    },
  ],
  message: [
    {
      validator: (value: string) => !!value.trim(),
      message: '请输入留言内容',
    },
  ],
}

// 处理手机号输入
const handlePhoneInput = (e: Event) => {
  const input = e.target as HTMLInputElement
  userInfoFormData.value.phone = input.value.replace(/[^\d]/g, '')
}

// 验证单个字段
const validateField = (field: keyof typeof validationRules) => {
  const rules = validationRules[field]
  const value = userInfoFormData.value[field]

  // 对该字段执行所有验证规则
  for (const rule of rules) {
    if (!rule.validator(value)) {
      errors.value[field] = rule.message
      return false
    }
  }

  // 验证通过，清除错误信息
  errors.value[field] = ''
  return true
}

// 处理字段失焦事件
const handleFieldBlur = (field: keyof typeof userInfoFormData.value) => {
  touchedFields.value[field] = true
  validateField(field as keyof typeof validationRules)
}

// 表单验证（提交时）
const validateForm = () => {
  let isValid = true

  // 标记所有字段为已触碰
  for (const field in touchedFields.value) {
    touchedFields.value[field as keyof typeof touchedFields.value] = true
  }

  // 遍历验证规则
  for (const field in validationRules) {
    if (!validateField(field as keyof typeof validationRules)) {
      isValid = false
    }
  }

  return isValid
}

// 提交表单
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  try {
    isSubmitting.value = true
    const { data, error } = await submitConsultation(userInfoFormData.value)

    // 检查是否有网络错误
    if (error.value) {
      toast.error(`提交失败: ${error.value.message || '请稍后重试'}`)
      return
    }

    // 检查响应数据，后端返回格式: {"code": "0", "data": null, "msg": "success"}
    if (data.value) {
      const response = data.value as any

      // 判断业务状态码，code 为 "0" 表示成功
      if (response.code === '0' || response.code === 0) {
        // 提交成功，重置表单
        userInfoFormData.value = {
          name: '',
          phone: '',
          email: '',
          company: '',
          industry: '',
          message: '',
        }

        // 显示成功提示
        toast.success('提交成功，我们将尽快与您取得联系')

        // 回到上一页
        setTimeout(() => {
          router.back()
        }, 1500)
      } else {
        // 业务错误
        toast.error(response.msg || '提交失败，请稍后重试')
      }
    } else {
      toast.warning('提交状态未知，请联系管理员确认')
    }
  } catch (err) {
    toast.error('提交失败，请稍后重试')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div
    class="md:hidden w-full min-h-screen bg-[#F9FBFF] pt-[120px] pb-[55px] px-[20px]"
  >
    <div class="w-full">
      <h1 class="text-[18px] font-medium text-[#000] leading-[28px] mb-[24px]">
        业务咨询及商务合作
      </h1>

      <form @submit.prevent="handleSubmit">
        <div :class="errors.name ? '' : 'mb-[29px]'">
          <label class="block text-[14px] text-[#000] font-normal mb-[4px]">
            姓名<span class="text-[#1CC9BE] ml-[3px]">*</span>
          </label>
          <input
            v-model="userInfoFormData.name"
            type="text"
            maxlength="16"
            placeholder="请填写您的名字"
            class="w-full h-[46px] px-[8px] py-[12px] bg-[#FFF] border rounded-[2px] text-[14px] text-[#333] placeholder:text-[#999999] focus:outline-none transition-colors"
            :class="
              touchedFields.name && errors.name
                ? 'border-[#FF5959]'
                : 'border-[#E5E5E5]'
            "
            @blur="handleFieldBlur('name')"
          />
          <p
            v-if="touchedFields.name && errors.name"
            class="text-[12px] text-[#FF5959] leading-[18px] mt-[4px] mb-[7px]"
          >
            {{ errors.name }}
          </p>
        </div>

        <!-- 联系电话 -->
        <div :class="errors.phone ? '' : 'mb-[29px]'">
          <label class="block text-[14px] text-[#000] font-normal mb-[4px]">
            联系电话<span class="text-[#1CC9BE] ml-[3px]">*</span>
          </label>
          <input
            v-model="userInfoFormData.phone"
            type="text"
            maxlength="11"
            placeholder="请填写您的电话"
            class="w-full h-[46px] px-[8px] py-[12px] bg-[#FFF] border rounded-[2px] text-[14px] text-[#333] placeholder:text-[#999999] focus:outline-none transition-colors"
            :class="
              touchedFields.phone && errors.phone
                ? 'border-[#FF5959]'
                : 'border-[#E5E5E5]'
            "
            @input="handlePhoneInput"
            @blur="handleFieldBlur('phone')"
          />
          <p
            v-if="touchedFields.phone && errors.phone"
            class="text-[12px] text-[#FF5959] leading-[18px] mt-[4px] mb-[7px]"
          >
            {{ errors.phone }}
          </p>
        </div>

        <!-- 电子邮箱 -->
        <div :class="errors.email ? '' : 'mb-[29px]'">
          <label class="block text-[14px] text-[#000] font-normal mb-[4px]">
            电子邮箱<span class="text-[#1CC9BE] ml-[3px]">*</span>
          </label>
          <input
            v-model="userInfoFormData.email"
            type="text"
            maxlength="64"
            placeholder="请填写您的电子邮箱"
            class="w-full h-[46px] px-[8px] py-[12px] bg-[#FFF] border rounded-[2px] text-[14px] text-[#333] placeholder:text-[#999999] focus:outline-none transition-colors"
            :class="
              touchedFields.email && errors.email
                ? 'border-[#FF5959]'
                : 'border-[#E5E5E5]'
            "
            @blur="handleFieldBlur('email')"
          />
          <p
            v-if="touchedFields.email && errors.email"
            class="text-[12px] text-[#FF5959] leading-[18px] mt-[4px] mb-[7px]"
          >
            {{ errors.email }}
          </p>
        </div>

        <!-- 单位 -->
        <div :class="errors.company ? '' : 'mb-[29px]'">
          <label class="block text-[14px] text-[#000] font-normal mb-[4px]">
            单位<span class="text-[#1CC9BE] ml-[3px]">*</span>
          </label>
          <input
            v-model="userInfoFormData.company"
            type="text"
            maxlength="16"
            placeholder="请填写您的单位"
            class="w-full h-[46px] px-[8px] py-[12px] bg-[#FFF] border rounded-[2px] text-[14px] text-[#333] placeholder:text-[#999999] focus:outline-none transition-colors"
            :class="
              touchedFields.company && errors.company
                ? 'border-[#FF5959]'
                : 'border-[#E5E5E5]'
            "
            @blur="handleFieldBlur('company')"
          />
          <p
            v-if="touchedFields.company && errors.company"
            class="text-[12px] text-[#FF5959] leading-[18px] mt-[4px] mb-[7px]"
          >
            {{ errors.company }}
          </p>
        </div>

        <!-- 行业类别 -->
        <div :class="errors.industry ? '' : 'mb-[29px]'">
          <label class="block text-[14px] text-[#000] font-normal mb-[4px]">
            行业类别<span class="text-[#1CC9BE] ml-[3px]">*</span>
          </label>
          <input
            v-model="userInfoFormData.industry"
            type="text"
            maxlength="16"
            placeholder="请填写您所在的行业"
            class="w-full h-[46px] px-[8px] py-[12px] bg-[#FFF] border rounded-[2px] text-[14px] text-[#333] placeholder:text-[#999999] focus:outline-none transition-colors"
            :class="
              touchedFields.industry && errors.industry
                ? 'border-[#FF5959]'
                : 'border-[#E5E5E5]'
            "
            @blur="handleFieldBlur('industry')"
          />
          <p
            v-if="touchedFields.industry && errors.industry"
            class="text-[12px] text-[#FF5959] leading-[18px] mt-[4px] mb-[7px]"
          >
            {{ errors.industry }}
          </p>
        </div>

        <!-- 留言内容 -->
        <div :class="errors.message ? 'mb-[8px]' : 'mb-[8px]'">
          <label class="block text-[14px] text-[#000] font-normal mb-[4px]">
            留言内容<span class="text-[#1CC9BE] ml-[3px]">*</span>
          </label>
          <div class="relative">
            <textarea
              v-model="userInfoFormData.message"
              maxlength="200"
              placeholder="请填写您的留言"
              rows="5"
              class="w-full py-[10px] px-[8px] bg-[#FFF] border rounded-[2px] text-[14px] text-[#333] placeholder:text-[#999999] focus:outline-none transition-colors resize-none"
              :class="
                touchedFields.message && errors.message
                  ? 'border-[#FF5959]'
                  : 'border-[#E5E5E5]'
              "
              @blur="handleFieldBlur('message')"
            />
            <span
              class="absolute bottom-[10px] right-[12px] text-[12px] text-[#999999]"
            >
              {{ userInfoFormData.message.length }}/200
            </span>
          </div>
          <p
            v-if="touchedFields.message && errors.message"
            class="text-[12px] text-[#FF5959] leading-[18px] mt-[4px] mb-[7px]"
          >
            {{ errors.message }}
          </p>
        </div>

        <!-- 提示文字 -->
        <p
          class="text-[10px] text-[#999999] mb-[12px] leading-[15px] text-center"
        >
          请认真填写留言信息，我们将会在24小时之内与您取得联系。
        </p>

        <!-- 提交按钮 -->
        <button
          type="submit"
          :disabled="isSubmitting"
          class="w-full h-[40px] text-[16px] text-[#FFF] leading-[40px] font-medium rounded-[2px] transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
          style="background: linear-gradient(90deg, #01aaf3 0%, #01ecd3 100%)"
        >
          {{ isSubmitting ? '提交中...' : '立即咨询' }}
        </button>
      </form>

      <!-- 联系我们区域 -->
      <div class="mt-[30px]">
        <h2
          class="text-[18px] font-medium text-[#000] leading-[28px] mb-[24px]"
        >
          联系我们
        </h2>

        <!-- 二维码区域 -->
        <div class="flex flex-col items-center mb-[24px]">
          <img
            src="https://images-cloud.qboson.com/website/qrod.png"
            alt="QR Code"
            class="w-[120px] h-[120px] object-contain mb-[12px]"
          />
          <span class="text-[12px] text-[#000] font-normal`"
            >专属商务联系人微信二维码</span
          >
        </div>

        <!-- 联系方式列表 -->
        <div class="flex flex-col gap-[12px]">
          <div
            class="flex items-center h-[46px] px-[12px] bg-[#F6F7F9] rounded-[2px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 30 30"
              fill="none"
            >
              <path
                d="M21.5971 27.0465C20.4186 27.0465 19.0716 26.8322 17.6635 26.3884C14.2961 25.3322 10.9747 23.1435 8.32672 20.22C4.34713 15.8271 2.43387 11.4496 2.81652 7.54653C2.92366 6.58224 3.24509 5.35775 4.69917 4.36285C5.81652 3.4751 7.08693 2.93938 8.08183 2.97C9.12264 2.9853 9.48999 3.35265 10.5614 4.92918C12.1992 7.19449 12.3216 8.32714 12.2604 8.97C12.1533 10.0414 11.5563 10.5006 11.0359 10.9139C10.8982 11.021 10.7757 11.1129 10.6686 11.22C10.0257 12.0465 9.22978 13.1792 12.6124 16.669C15.5053 19.4394 17.0512 19.7761 17.7094 19.7302C18.2145 19.6996 18.4288 19.4241 18.5053 19.3322L18.5206 19.3169C19.592 17.8016 19.9594 17.4343 20.9849 17.2812C21.9951 17.1282 22.5002 17.4343 24.6277 18.7812C26.5563 19.9904 27.4441 20.8016 27.2298 22.0567C27.1686 23.4496 26.1124 25.3322 24.9033 26.1741C24.0767 26.7557 22.9441 27.0465 21.5971 27.0465ZM8.0053 4.17918C7.45428 4.17918 6.47468 4.47 5.41856 5.32714L5.38795 5.35775C4.40836 6.01591 4.13285 6.76591 4.0257 7.66898C3.68897 11.2047 5.47979 15.2608 9.22979 19.3935C13.3012 23.8782 18.3522 25.822 21.6277 25.822C22.7145 25.822 23.6022 25.6078 24.1992 25.1945C25.1788 24.5057 25.9747 22.8986 26.0053 21.9955L26.0206 21.9037C26.0818 21.5516 26.143 21.1996 23.9696 19.8373C21.8879 18.521 21.6737 18.4292 21.1686 18.5057C20.6482 18.5822 20.541 18.5975 19.5155 20.0516L19.4849 20.0822C18.9645 20.7404 18.291 20.9241 17.8165 20.97C16.2706 21.0924 14.2349 19.9445 11.7706 17.572L11.7553 17.5567C7.88285 13.5771 8.69407 11.8016 9.73489 10.4547L9.81142 10.3782C9.97979 10.2251 10.1328 10.0873 10.3012 9.96489C10.7757 9.58224 11.0053 9.38326 11.0665 8.86285C11.1277 8.15877 10.6226 7.07204 9.58183 5.63326L9.56652 5.61795C8.60223 4.19449 8.5104 4.19449 8.06652 4.19449C8.03591 4.17918 8.0206 4.17918 8.0053 4.17918Z"
                fill="#666"
              />
            </svg>
            <span class="ml-[13px] text-[14px] text-[#000]">15810326030</span>
          </div>
          <div
            class="flex items-center h-[46px] px-[16px] bg-[#F6F7F9] rounded-[2px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 30 30"
              fill="none"
            >
              <path
                d="M24.3298 9.30091C24.5984 9.10021 24.9797 9.16663 25.1587 9.41353C25.36 9.68215 25.293 10.0634 25.0244 10.2424L15.5256 17.1419C15.3018 17.2986 15.0332 17.2763 14.831 17.1419L5.31056 10.2424C5.04194 10.0634 4.99718 9.68221 5.17628 9.41359C5.37848 9.16669 5.73656 9.10027 6.00512 9.30097L15.1675 15.9319L24.3298 9.30091ZM5.01962 5.17969H25.3153C25.9875 5.17969 26.5926 5.44831 27.018 5.89591C27.444 6.32191 27.7125 6.92701 27.7125 7.57681V22.6979C27.7125 23.347 27.4439 23.952 27.018 24.4004C26.5702 24.8257 25.9875 25.0942 25.3153 25.0942H5.01962C4.3697 25.0942 3.76544 24.8257 3.3395 24.4004C2.89184 23.952 2.62256 23.3694 2.62256 22.6979V7.57675C2.62256 6.92695 2.89184 6.32185 3.3395 5.89585C3.76544 5.44825 4.36976 5.17969 5.01962 5.17969ZM25.3153 6.36667H5.01962C4.68386 6.36667 4.39214 6.50095 4.16834 6.72475C3.94454 6.94855 3.8102 7.24033 3.8102 7.57675V22.6979C3.8102 23.0336 3.94454 23.3469 4.16834 23.5491C4.39214 23.773 4.68386 23.9072 5.01962 23.9072H25.3153C25.6518 23.9072 25.9652 23.773 26.1891 23.5491C26.3904 23.347 26.5255 23.0336 26.5255 22.6979V7.57675C26.5255 7.24033 26.3904 6.94855 26.1891 6.72475C25.9652 6.50095 25.6518 6.36667 25.3153 6.36667ZM14.831 17.1419C14.7646 17.0972 14.7199 17.0524 14.6967 17.0077Z"
                fill="#666"
              />
            </svg>
            <span class="ml-[13px] text-[14px] text-[#000]"
              >jujw@boseq.com</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 移除输入框自动填充的背景色 */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px white inset;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
